import { useState, useCallback } from 'react';
import { NotificationProps, NotificationType } from '../components/Notification';

interface NotificationOptions {
  title: string;
  message?: string;
  duration?: number;
}

export const useNotification = () => {
  const [notifications, setNotifications] = useState<Omit<NotificationProps, 'onClose'>[]>([]);

  const showNotification = useCallback((type: NotificationType, options: NotificationOptions) => {
    const id = `notification-${Date.now()}-${Math.random()}`;
    const notification: Omit<NotificationProps, 'onClose'> = {
      id,
      type,
      title: options.title,
      message: options.message,
      duration: options.duration ?? 5000,
    };

    setNotifications((prev) => [...prev, notification]);

    return id;
  }, []);

  const showSuccess = useCallback((options: NotificationOptions) => {
    return showNotification('success', options);
  }, [showNotification]);

  const showError = useCallback((options: NotificationOptions) => {
    return showNotification('error', options);
  }, [showNotification]);

  const showWarning = useCallback((options: NotificationOptions) => {
    return showNotification('warning', options);
  }, [showNotification]);

  const showInfo = useCallback((options: NotificationOptions) => {
    return showNotification('info', options);
  }, [showNotification]);

  const closeNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  return {
    notifications,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    closeNotification,
    clearAllNotifications,
  };
};
